import { Pause, Play } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { RecordingResult } from '../audio/recorder';
import { matchRecording } from '../audio/matchBrowser';
import { MatchMeter } from './MatchMeter';
import { player } from '../audio/player';
import { usePlayback } from '../audio/usePlayback';
import type { Clip } from '../content/types';
import { barsIn, speechRange, resampleBars } from '../lib/voice';

interface TrackProps {
  name: string;
  tag: string;
  src: string;
  bars: number[];
  range: { from: number; to: number };
  you?: boolean;
}

function Track({ name, tag, src, bars, range, you }: TrackProps) {
  const { playing, progress } = usePlayback(tag);
  const lit = Math.round(progress * bars.length);
  return (
    <div className={`track ${you ? 'track-you' : ''}`}>
      <button
        type="button"
        className="track-play"
        aria-label={playing ? `Stop ${name}` : `Play ${name}`}
        aria-pressed={playing}
        onClick={() => (playing ? player.stop() : player.play(src, { ...range, tag }))}
      >
        {playing ? <Pause size={18} aria-hidden="true" fill="currentColor" strokeWidth={0} /> : <Play size={18} aria-hidden="true" fill="currentColor" strokeWidth={1.5} strokeLinejoin="round" style={{ marginLeft: 2 }} />}
      </button>
      <div className="track-meta">
        <span className="track-name">{name}</span>
        <div className="wave" aria-hidden="true">
          {bars.map((b, i) => (
            <span key={i} data-on={playing && i < lit} style={{ height: `${Math.max(8, b)}%` }} />
          ))}
        </div>
      </div>
    </div>
  );
}

interface Props {
  clip: Clip;
  /** Every voice of the phrase: the recording is matched against each and the best counts. */
  models?: Clip[];
  voiceName: string;
  recording: RecordingResult | null;
  /** Reports the match once it is known (null when no speech was heard), with the recording it is for. */
  onMatch?: (score: number | null, recordingUrl: string) => void;
  /** Shown under the learner's recording, e.g. the button to record again. */
  action?: ReactNode;
  /** Changing this plays the model, then the recording. */
  autoPlay?: number;
  id: string;
}

/** The model and the learner's recording, one above the other, each playable; plays both in turn on demand. */
export function Compare({ clip, models, voiceName, recording, autoPlay, id, onMatch, action }: Props) {
  const modelRange = useMemo(() => speechRange(clip), [clip]);
  const modelBars = useMemo(() => resampleBars(barsIn(clip.peaks, clip.duration, modelRange)), [clip, modelRange]);
  const youBars = useMemo(
    () => resampleBars(recording && recording.peaks.length ? barsIn(recording.peaks, recording.duration, recording.speech) : []),
    [recording],
  );

  const [blocked, setBlocked] = useState(false);
  const [score, setScore] = useState<number | null | undefined>(undefined);
  useEffect(() => {
    if (!recording) return;
    let live = true;
    setScore(undefined);
    const srcs = (models?.length ? models : [clip]).map((c) => c.src);
    matchRecording(recording.blob, srcs)
      .then((r) => {
        if (!live) return;
        setScore(r.score);
        onMatch?.(r.score, recording.url);
      })
      .catch(() => {
        // Decoding failed (an unusual recording format): leave the comparison to the learner's ears.
        if (live) setScore(-1);
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording?.url]);
  useEffect(() => {
    if (!autoPlay) return;
    const items = [{ src: clip.src, ...modelRange, tag: `${id}:model` }];
    if (recording) items.push({ src: recording.url, ...recording.speech, tag: `${id}:you` });
    player.sequence(items).then((r) => setBlocked(r === 'blocked'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlay]);

  return (
    <div className="compare" role="group" aria-label="Compare with the model">
      <Track name={`Model · ${voiceName}`} tag={`${id}:model`} src={clip.src} bars={modelBars} range={modelRange} />
      {recording && <Track name="You" tag={`${id}:you`} src={recording.url} bars={youBars} range={recording.speech} you />}
      {recording && score !== -1 && <MatchMeter score={score} />}
      {action}
      {blocked && <p className="small muted">Tap the play buttons to hear the model, then yourself.</p>}
    </div>
  );
}
